/**
 * PUNISH'D THEME - CART FUNCTIONALITY
 * Handles cart drawer, add to cart, and cart updates
 */

(function() {
  'use strict';

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.cartIcon = document.querySelector('[data-cart-icon]');
      this.cartCount = document.querySelector('[data-cart-count]');
      this.closeButtons = document.querySelectorAll('[data-cart-drawer-close]');
      this.cartItems = document.querySelector('[data-cart-items]');
      this.subtotal = document.querySelector('[data-cart-subtotal]');
      this.shippingContainer = this.drawer ? this.drawer.querySelector('[data-shipping-container]') : null;
      this.shippingText = this.drawer ? this.drawer.querySelector('[data-shipping-text]') : null;
      this.shippingProgress = this.drawer ? this.drawer.querySelector('[data-shipping-progress]') : null;
      this.freeShippingThreshold = this.drawer && this.drawer.dataset.freeShippingThreshold
        ? parseInt(this.drawer.dataset.freeShippingThreshold, 10)
        : null;
      this.noteInput = this.drawer ? this.drawer.querySelector('[data-cart-note]') : null;

      this.init();
    }

    init() {
      if (!this.drawer) return;

      // Open cart drawer when cart icon is clicked
      if (this.cartIcon) {
        this.cartIcon.addEventListener('click', (e) => {
          // Default behavior is to go to cart page
          // If you want drawer behavior, uncomment the next lines:
          // e.preventDefault();
          // this.open();
        });
      }

      // Close cart drawer
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
          this.close();
        }
      });

      // Listen for cart updates
      document.addEventListener('cart:updated', () => this.updateCart());

      // Initialize quantity controls
      this.initQuantityControls();

      // Initialize remove buttons
      this.initRemoveButtons();
    }

    open() {
      this.drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.updateCart();
    }

    close() {
      this.drawer.classList.remove('active');
      document.body.style.overflow = '';
    }

    async updateCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        this.updateCartCount(cart.item_count);
        this.renderCartItems(cart);
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    }

    updateCartCount(count) {
      if (this.cartCount) {
        this.cartCount.textContent = count;
        this.cartCount.style.display = count > 0 ? 'flex' : 'none';
      }
    }

    async renderCartItems(cart) {
      if (!this.cartItems) return;

      if (cart.item_count === 0) {
        this.cartItems.innerHTML = `
          <div class="cart-empty">
            <p>Your cart is empty. That's sad. Or is it?</p>
            <a href="/collections/all" class="btn btn-primary">Start Shopping</a>
          </div>
        `;
        this.updateSubtotal(cart.total_price);
        this.updateShippingProgress(cart.total_price);
        if (this.noteInput) {
          this.noteInput.value = '';
        }
        return;
      }

      this.cartItems.innerHTML = this.buildCartItems(cart);
      this.updateSubtotal(cart.total_price);
      this.updateShippingProgress(cart.total_price);

      if (this.noteInput) {
        this.noteInput.value = cart.note || '';
      }
    }

    buildCartItems(cart) {
      const cartAction = (window.routes && window.routes.cart_url) || '/cart';
      const itemsMarkup = cart.items.map((item, index) => this.buildCartItem(item, index)).join('');

      return `
        <form action="${cartAction}" method="post" id="CartDrawerForm">
          ${itemsMarkup}
        </form>
      `;
    }

    buildCartItem(item, index) {
      const line = index + 1;
      const hasImage = Boolean(item.image);
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="cart-item-variant">${CartDrawer.escapeHtml(item.variant_title)}</div>`
        : '';

      const properties = this.buildPropertiesList(item.properties);

      const price = item.original_price !== item.final_price
        ? `<span class="cart-item-price-sale">${this.formatMoney(item.final_price)}</span>
           <s class="cart-item-price-compare">${this.formatMoney(item.original_price)}</s>`
        : this.formatMoney(item.final_price);

      return `
        <div class="cart-item" data-cart-item data-line="${line}">
          <div class="cart-item-media">
            ${hasImage ? `<img src="${item.image}" alt="${CartDrawer.escapeHtml(item.product_title)}" width="100" height="125" loading="lazy">` : ''}
          </div>
          <div class="cart-item-details">
            <a href="${item.url}" class="cart-item-title">${CartDrawer.escapeHtml(item.product_title)}</a>
            ${variantTitle}
            ${properties}
            <div class="cart-item-price">
              ${price}
            </div>
            <div class="cart-item-quantity">
              <button type="button" class="quantity-btn" data-quantity-decrease data-line="${line}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z"/>
                </svg>
              </button>
              <input
                type="number"
                name="updates[]"
                value="${item.quantity}"
                min="0"
                class="quantity-input"
                data-quantity-input
                data-line="${line}"
              >
              <button type="button" class="quantity-btn" data-quantity-increase data-line="${line}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                </svg>
              </button>
            </div>
          </div>
          <button type="button" class="cart-item-remove" data-cart-remove data-line="${line}">
            Remove
          </button>
        </div>
      `;
    }

    buildPropertiesList(properties) {
      if (!properties) return '';

      const entries = Object.entries(properties).filter(([key, value]) => {
        if (!value) return false;
        return key.charAt(0) !== '_';
      });

      if (!entries.length) return '';

      const list = entries.map(([key, value]) => `
        <dt>${CartDrawer.escapeHtml(key)}:</dt>
        <dd>${CartDrawer.escapeHtml(value)}</dd>
      `).join('');

      return `
        <dl class="cart-item-properties">
          ${list}
        </dl>
      `;
    }

    updateSubtotal(amount) {
      if (!this.subtotal) return;
      this.subtotal.textContent = this.formatMoney(amount);
    }

    updateShippingProgress(totalPrice) {
      if (!this.shippingContainer || !this.shippingText || !this.shippingProgress || !this.freeShippingThreshold) {
        return;
      }

      if (totalPrice >= this.freeShippingThreshold) {
        this.shippingText.textContent = '🎉 You\'ve qualified for free shipping!';
        this.shippingProgress.style.width = '100%';
        return;
      }

      const remaining = this.freeShippingThreshold - totalPrice;
      const percentage = Math.min(100, (totalPrice / this.freeShippingThreshold) * 100);
      this.shippingText.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more for free shipping!`;
      this.shippingProgress.style.width = `${percentage}%`;
    }

    formatMoney(value) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(value);
      }

      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      const amount = value / 100;
      return amount.toLocaleString(undefined, {
        style: 'currency',
        currency: currency
      });
    }

    static escapeHtml(str) {
      if (typeof str !== 'string') return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    initQuantityControls() {
      document.addEventListener('click', async (e) => {
        const increaseBtn = e.target.closest('[data-quantity-increase]');
        const decreaseBtn = e.target.closest('[data-quantity-decrease]');

        if (increaseBtn) {
          const line = increaseBtn.dataset.line;
          const input = document.querySelector(`[data-quantity-input][data-line="${line}"]`);
          if (input) {
            const newQty = parseInt(input.value) + 1;
            await this.updateLineItem(line, newQty);
          }
        }

        if (decreaseBtn) {
          const line = decreaseBtn.dataset.line;
          const input = document.querySelector(`[data-quantity-input][data-line="${line}"]`);
          if (input) {
            const newQty = Math.max(0, parseInt(input.value) - 1);
            await this.updateLineItem(line, newQty);
          }
        }
      });

      // Handle direct input changes
      document.addEventListener('change', async (e) => {
        const input = e.target.closest('[data-quantity-input]');
        if (input) {
          const line = input.dataset.line;
          const newQty = parseInt(input.value);
          await this.updateLineItem(line, newQty);
        }
      });
    }

    initRemoveButtons() {
      document.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          const line = removeBtn.dataset.line;
          await this.updateLineItem(line, 0);
        }
      });
    }

    async updateLineItem(line, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: line,
            quantity: quantity
          })
        });

        if (response.ok) {
          const cart = await response.json();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          this.updateCart();
        }
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    }
  }

  // Added to Cart Modal
  class AddedToCartModal {
    constructor() {
      this.modal = document.querySelector('[data-added-to-cart-modal]');
      this.closeButtons = document.querySelectorAll('[data-added-to-cart-modal-close]');
      this.init();
    }

    init() {
      if (!this.modal) return;

      // Close buttons
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on click outside content
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('active')) {
          this.close();
        }
      });
    }

    open() {
      if (!this.modal) return;
      this.modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  // Product form handling
  class ProductForm {
    constructor() {
      this.forms = document.querySelectorAll('[id^="ProductForm"]');
      this.init();
    }

    init() {
      this.forms.forEach(form => {
        form.addEventListener('submit', (e) => this.handleSubmit(e));
      });

      // Variant selection
      document.querySelectorAll('[data-option-select]').forEach(select => {
        select.addEventListener('change', () => this.updateVariant());
      });
    }

    async handleSubmit(e) {
      e.preventDefault();

      const form = e.target;
      const submitButton = form.querySelector('[data-add-to-cart]');
      const formData = new FormData(form);

      // Disable button during request
      submitButton.disabled = true;
      const originalText = submitButton.querySelector('[data-add-to-cart-text]').textContent;
      submitButton.querySelector('[data-add-to-cart-text]').textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const item = await response.json();

          // Show success message (you can customize this)
          submitButton.querySelector('[data-add-to-cart-text]').textContent = 'Added!';

          // Trigger cart update
          document.dispatchEvent(new CustomEvent('cart:updated'));

          // Open Added to Cart Modal instead of drawer
          let modal = window.addedToCartModal;
          
          // Fallback: If global instance missing, try to find element directly
          if (!modal) {
            const modalEl = document.querySelector('[data-added-to-cart-modal]');
            if (modalEl) {
              modal = {
                open: () => {
                  modalEl.classList.add('active');
                  document.body.style.overflow = 'hidden';
                }
              };
            }
          }

          if (modal) {
            modal.open();
          } else {
            // Fallback to drawer if modal absolutely cannot be found
            const cartDrawer = document.querySelector('[data-cart-drawer]');
            if (cartDrawer) {
               cartDrawer.classList.add('active');
            }
          }

          // Reset button after delay
          setTimeout(() => {
            submitButton.querySelector('[data-add-to-cart-text]').textContent = originalText;
            submitButton.disabled = false;
          }, 1000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        submitButton.querySelector('[data-add-to-cart-text]').textContent = 'Error - Try Again';
        submitButton.disabled = false;
      }
    }

    updateVariant() {
      // Get all selected options
      const selects = document.querySelectorAll('[data-option-select]');
      const selectedOptions = Array.from(selects).map(select => select.value);

      // Find matching variant (simplified - in production, use Shopify's variant selection)
      const productSelect = document.querySelector('[data-product-select]');
      if (productSelect) {
        const options = Array.from(productSelect.options);
        const matchingOption = options.find(option => {
          return selectedOptions.every(selected => option.text.includes(selected));
        });

        if (matchingOption) {
          productSelect.value = matchingOption.value;
        }
      }
    }
  }

  // Collection sorting and filtering
  class CollectionFilters {
    constructor() {
      this.sortSelect = document.querySelector('[data-sort-select]');
      this.init();
    }

    init() {
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', (e) => {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', e.target.value);
          window.location.href = url.toString();
        });
      }

      // Filter form submission
      const filterForm = document.querySelector('#FacetFiltersForm');
      if (filterForm) {
        filterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const formData = new FormData(filterForm);
          const url = new URL(window.location.href);

          // Clear existing filter params
          for (let key of url.searchParams.keys()) {
            if (key.startsWith('filter.')) {
              url.searchParams.delete(key);
            }
          }

          // Add new filter params
          for (let [key, value] of formData.entries()) {
            if (value) {
              url.searchParams.append(key, value);
            }
          }

          window.location.href = url.toString();
        });

        // Auto-submit on checkbox change
        filterForm.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
          checkbox.addEventListener('change', () => {
            filterForm.dispatchEvent(new Event('submit'));
          });
        });
      }
    }
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawer();
    window.addedToCartModal = new AddedToCartModal();
    new ProductForm();
    new CollectionFilters();
  });

})();